import { Suspense, lazy, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowUpRight, Briefcase, Calculator, Crown, Flame, Globe, History, Layers, Lock, Newspaper, Orbit, TrendingDown, TrendingUp, Wheat, Zap } from "lucide-react";
import Logo3D from "@/components/Logo3D";
import StrengthGauge from "@/components/StrengthGauge";
import { API, useRx } from "@/context/AuthContext";
import { fmt } from "@/lib/format";

const HeroEmblem = lazy(() => import("@/immersive/HeroEmblem"));

const SECTIONS = [
  [TrendingUp, "Option Buyer", "/app/option-buyer", "CE / PE strikes with SL, target & trailing"],
  [TrendingDown, "Option Seller", "/app/option-seller", "Sell strikes, strangles & hedges"],
  [Zap, "Stock Intraday", "/app/intraday", "Momentum calls on liquid F&O stocks"],
  [Layers, "Futures", "/app/futures", "Spot vs futures premium + OI build-up"],
  [Wheat, "Multi-Commodity", "/app/commodity", "MCX metals, energy & Agri / Anaj"],
  [Briefcase, "Investors Window", "/app/investors", "Delivery picks & BTST filters"],
  [Crown, "Pro Trader Desk", "/app/pro-desk", "Build & backtest your own strategies"],
  [Orbit, "Immersive 3D / AR", "/app/immersive", "Strength orb & spot-futures planes"],
];
const EXTRAS = [[Flame, "Liquid Options", "/app/liquid-options"], [Newspaper, "Live News", "/app/news"], [History, "Signal History", "/app/performance"], [Calculator, "Risk Calculator", "/app/tools"]];

function usePulse() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    const run = () => axios.get(`${API}/public/pulse`).then((r) => alive && setD(r.data)).catch(() => {});
    run();
    const id = setInterval(run, 3000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  return d;
}

function Nav({ authed }) {
  return (
    <header className="fixed inset-x-0 top-0 z-30">
      <div className="max-w-7xl mx-auto mt-3 mx-3 lg:mx-auto rx-glass rounded-2xl flex items-center justify-between px-4 sm:px-6 h-16">
        <Link to="/"><Logo3D size={32} /></Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-gray-400">
          <a href="#pulse" className="hover:text-white transition-colors">Market Pulse</a>
          <a href="#sections" className="hover:text-white transition-colors">Windows</a>
          <Link to="/pricing" className="hover:text-white transition-colors" data-testid="landing-pricing-link">Pricing</Link>
        </nav>
        {authed ? (
          <Link to="/app" data-testid="landing-open-app-btn" className="text-sm font-bold rounded-full bg-[#00FF66] text-black px-5 py-2 hover:shadow-[0_0_24px_rgba(0,255,102,.5)] transition-shadow">Open Command Center</Link>
        ) : (
          <div className="flex items-center gap-2">
            <Link to="/sign-in" data-testid="landing-signin-btn" className="text-sm text-gray-300 hover:text-white px-3">Sign in</Link>
            <Link to="/sign-up" data-testid="landing-signup-btn" className="text-sm font-bold rounded-full bg-white text-black px-5 py-2 hover:bg-[#00FF66] transition-colors">Start free</Link>
          </div>
        )}
      </div>
    </header>
  );
}

function PulseStrip({ pulse }) {
  return (
    <section id="pulse" className="relative max-w-7xl mx-auto px-6 -mt-24 z-10" data-testid="landing-pulse">
      <div className="rx-glass rounded-3xl p-5 lg:p-7 shadow-[0_30px_80px_rgba(0,0,0,.6)]">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <span className="relative flex h-2.5 w-2.5"><span className="absolute inset-0 rounded-full bg-[#00FF66] rx-pulse-ring" /><span className="relative h-2.5 w-2.5 rounded-full bg-[#00FF66]" /></span>
            <span className="font-display font-bold tracking-tight">Live Market Pulse</span>
          </div>
          {pulse && (
            <div className="font-data text-xs text-gray-400" data-testid="landing-net-impact">
              Global cues → Nifty <span className={pulse.global.nifty_impact_pts >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}>{pulse.global.nifty_impact_pts >= 0 ? "+" : ""}{fmt(pulse.global.nifty_impact_pts)} pts</span> · {pulse.global.outlook}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {(pulse?.indices || Array.from({ length: 4 }).map((_, i) => ({ symbol: i }))).map((ix) => (
            <div key={ix.symbol} className="rx-card p-4 rx-lift" data-testid={`landing-index-${ix.symbol}`}>
              {ix.name ? (
                <>
                  <div className="flex justify-between items-start">
                    <div className="font-display text-sm font-bold">{ix.symbol}</div>
                    <span className={`font-data text-xs ${ix.chg_pct >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{ix.chg_pct >= 0 ? "+" : ""}{fmt(ix.chg_pct)}%</span>
                  </div>
                  <div className="font-data text-lg mt-1">{fmt(ix.ltp)}</div>
                  <div className="flex justify-center mt-1"><StrengthGauge score={ix.score} size={130} label="Strength" testId={`landing-gauge-${ix.symbol}`} /></div>
                </>
              ) : <div className="h-44 animate-pulse" />}
            </div>
          ))}
        </div>
        {pulse && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            {pulse.global.factors.map((f) => (
              <div key={f.symbol} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                <div><div className="text-xs text-gray-500">{f.name}</div><div className="font-data text-sm">{fmt(f.ltp, f.symbol === "USDINR" ? 4 : 2)} <span className={f.chg_pct >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}>{fmt(f.chg_pct)}%</span></div></div>
                <div className={`font-data text-xs ${f.impact_pts >= 0 ? "text-[#00FF66]" : "text-[#FF3366]"}`}>{f.impact_pts >= 0 ? "+" : ""}{fmt(f.impact_pts)} pts</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function Landing() {
  const { authed } = useRx();
  const [params] = useSearchParams();
  const pulse = usePulse();
  useEffect(() => { const r = params.get("ref"); if (r) localStorage.setItem("robox_ref", r.toUpperCase()); }, [params]);

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden" data-testid="landing-page">
      <Nav authed={authed} />
      <section className="relative min-h-[100vh] flex items-center rx-noise overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(800px_500px_at_70%_40%,rgba(0,122,255,.18),transparent_60%),radial-gradient(700px_500px_at_25%_70%,rgba(0,255,102,.12),transparent_60%)]" />
        <div className="rx-perspective-floor" />
        <div className="absolute inset-0 top-[45%] opacity-50 sm:top-0 sm:opacity-70 lg:opacity-100 lg:left-[46%]">
          <Suspense fallback={<div className="absolute inset-0 grid place-items-center"><Logo3D size={120} wordmark={false} testId="hero-logo-fallback" /></div>}>
            <HeroEmblem />
          </Suspense>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent lg:via-[#050505]/40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 w-full pt-28 pb-40 pointer-events-none">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-xl pointer-events-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#00FF66]/30 bg-[#00FF66]/5 px-3 py-1 text-[11px] font-bold tracking-[0.2em] text-[#00FF66]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#00FF66] animate-pulse" /> SIGNAL ENGINE · INDIAN MARKETS
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mt-6 leading-[1.02]">
              Trade the signal.<br /><span className="rx-text-glow">Not the noise.</span>
            </h1>
            <p className="text-gray-300 mt-6 text-base lg:text-lg max-w-lg">Live Strength Scores from spot vs futures, volume, OI and indicators — delivered as clean calls with strike, stop loss, target and trailing levels.</p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to={authed ? "/app" : "/sign-up"} data-testid="hero-start-btn" className="rounded-full bg-[#00FF66] text-black font-bold px-8 py-3.5 hover:shadow-[0_0_32px_rgba(0,255,102,.55)] transition-shadow">{authed ? "Enter Command Center" : "Open free account"}</Link>
              <Link to={authed ? "/app/immersive" : "/demo"} data-testid="hero-demo-btn" className="rounded-full border border-[#007AFF]/50 text-white px-8 py-3.5 font-semibold hover:bg-[#007AFF]/10 hover:shadow-[0_0_28px_rgba(0,122,255,.4)] transition-shadow">{authed ? "Immersive Mode" : "Reviewer demo"}</Link>
            </div>
            <div className="flex flex-wrap items-center gap-6 mt-10 text-xs text-gray-500">
              <span className="flex items-center gap-2"><Lock size={14} /> Strategy stays server-side</span>
              <span className="flex items-center gap-2"><Orbit size={14} /> 3D + AR mode</span>
              <span className="flex items-center gap-2"><Globe size={14} /> Dow · Crude · USDINR</span>
            </div>
          </motion.div>
        </div>
      </section>

      <PulseStrip pulse={pulse} />

      <section id="sections" className="max-w-7xl mx-auto px-6 py-24">
        <div className="rx-label text-[#007AFF]">Quick access</div>
        <h2 className="font-display text-3xl lg:text-5xl font-bold tracking-tight mt-3 max-w-2xl">One engine. <span className="rx-text-glow">Every trading style.</span></h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {SECTIONS.map(([Icon, t, to, d], i) => (
            <motion.div key={t} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={to} data-testid={`landing-section-${to.split("/").pop()}`} className="group rx-card rx-lift p-6 h-full flex flex-col relative overflow-hidden">
                <span className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[#00FF66]/0 group-hover:bg-[#00FF66]/10 blur-2xl transition-colors duration-500" />
                <span className="h-11 w-11 rounded-xl border border-white/10 bg-gradient-to-br from-[#00FF66]/15 to-[#007AFF]/15 grid place-items-center"><Icon className="text-[#00FF66]" size={20} /></span>
                <div className="font-display font-bold mt-5 flex items-center gap-2">{t}<ArrowUpRight size={14} className="text-gray-600 group-hover:text-[#00FF66] transition-colors" /></div>
                <p className="text-sm text-gray-400 mt-2">{d}</p>
              </Link>
            </motion.div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-6">
          {EXTRAS.map(([Icon, t, to]) => (
            <Link key={t} to={to} className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-gray-300 hover:text-white hover:border-[#007AFF]/50 transition-colors"><Icon size={14} className="text-[#007AFF]" />{t}</Link>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rx-hero-card p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <div className="rx-label text-[#00FF66]">Plans</div>
            <h3 className="font-display text-2xl lg:text-3xl font-bold mt-2">Basic ₹999 · Pro ₹1,799 <span className="text-gray-500 text-lg font-normal">/ month</span></h3>
            <p className="text-gray-400 mt-2 text-sm">Save 10% quarterly and 20% annually. Pro unlocks the Pro Trader Desk.</p>
          </div>
          <Link to="/pricing" data-testid="landing-see-plans-btn" className="rounded-full bg-white text-black font-bold px-7 py-3 hover:bg-[#00FF66] transition-colors">Compare plans</Link>
        </div>
      </section>
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-600">
          <Logo3D size={24} textClass="text-sm" testId="footer-logo" />
          <span>Market data shown is a simulated feed · Educational only, not investment advice</span>
        </div>
      </footer>
    </div>
  );
}
